import type { Translate } from 'next-translate';

import type {
  MarkyWebsiteFaq,
  MarkyWebsiteUseCase,
} from '@/constants/markyWebsiteUseCases';

export const translateMarkyWebsiteText = (
  t: Translate,
  value: string,
): string => t(value, null, { default: value });

export const translateMarkyWebsiteFaqs = (
  t: Translate,
  faqs: MarkyWebsiteFaq[],
): MarkyWebsiteFaq[] =>
  faqs.map((faq) => ({
    question: translateMarkyWebsiteText(t, faq.question),
    answer: translateMarkyWebsiteText(t, faq.answer),
  }));

export const translateMarkyWebsiteUseCase = (
  t: Translate,
  useCase: MarkyWebsiteUseCase,
): MarkyWebsiteUseCase => {
  const name = translateMarkyWebsiteText(t, useCase.name);
  const withWebsiteTypeFallback = (
    value: string,
    fallbackKey: string,
    fallback: string,
  ): string =>
    t(
      value,
      { websiteType: name },
      {
        default: t(fallbackKey, { websiteType: name }, { default: fallback }),
      },
    );

  return {
    ...useCase,
    name,
    shortName: translateMarkyWebsiteText(t, useCase.shortName),
    eyebrow: translateMarkyWebsiteText(t, useCase.eyebrow),
    seoTitle: translateMarkyWebsiteText(t, useCase.seoTitle),
    metaDescription: withWebsiteTypeFallback(
      useCase.metaDescription,
      'Build {{websiteType}} from chat with a database, authentication, editable code and managed deployment.',
      useCase.metaDescription,
    ),
    primaryKeyword: translateMarkyWebsiteText(t, useCase.primaryKeyword),
    heroTitle: translateMarkyWebsiteText(t, useCase.heroTitle),
    heroDescription: withWebsiteTypeFallback(
      useCase.heroDescription,
      'Describe the users and workflow. Marky turns the brief into an editable full-stack project with real data and a path to production.',
      useCase.heroDescription,
    ),
    definition: withWebsiteTypeFallback(
      useCase.definition,
      '{{websiteType}} turns a written brief into an editable web application. Marky can create the interface, backend, database, authentication and deployment configuration in one project.',
      useCase.definition,
    ),
    cardDescription: withWebsiteTypeFallback(
      useCase.cardDescription,
      'A tailored web application with the workflow, accounts and persistent data your project needs.',
      useCase.cardDescription,
    ),
    imageAlt: withWebsiteTypeFallback(
      useCase.imageAlt,
      'Illustration of {{websiteType}}',
      useCase.imageAlt,
    ),
    features: useCase.features.map((feature) => ({
      title: translateMarkyWebsiteText(t, feature.title),
      description: translateMarkyWebsiteText(t, feature.description),
    })),
    dataModel: useCase.dataModel.map((item) => ({
      entity: translateMarkyWebsiteText(t, item.entity),
      purpose: translateMarkyWebsiteText(t, item.purpose),
    })),
    buildSteps: useCase.buildSteps.map((step) => ({
      title: translateMarkyWebsiteText(t, step.title),
      description: translateMarkyWebsiteText(t, step.description),
    })),
    buildBrief: translateMarkyWebsiteText(t, useCase.buildBrief),
    faqs: translateMarkyWebsiteFaqs(t, useCase.faqs),
  };
};
